import { Button, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Field, Formik } from 'formik';
import type { FormikHelpers } from 'formik';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import * as apiDocument from 'api/apiDocument';
import * as apiObject from 'api/apiObjects';
import { getCloudflareUploadUrls, linkCloudflareFiles } from 'api/apiCloudflare';
import { useToast } from 'context/ToastContext';
import { FilesCloudUpload } from 'modules/files';
import type { LeadCloudFileType } from '@econtractor/types';
import { uploadFilesWithOverallProgress } from 'utils/helpers/featchFiles/uploadFileWithProgress';
import { safeFloatParse } from 'utils/helpers/safeParsing';
import { formatPrice } from 'utils/helpers/functions/formatPrice';
import InputPrice from 'components/others/InputPrice';
import {
    getBaseCurrencyPrefix,
    getBaseCurrencySymbol,
    getCurrencySymbol,
    getLocalCurrencyPrefix,
} from 'utils/helpers/currency/currencyHelpers';
import useSettingsParams from 'hooks/useSettingsParams';
import { useObjectCard } from '../../context/ObjectCardProvider';
import spinnerTitle from 'components/others/spinnerTitle';
import SolarIcon from 'components/icons/SolarIcon';

interface PaymentFormValues {
    paymentAmount: number;
    description: string;
}

interface Props {
    objectId: number;
    generationId: number;
    activeTab: string;
    setActiveTab: Dispatch<SetStateAction<string>>;
    recommendAmount: number;
    isUsd: boolean;
    fetchPayments: () => void;
    onPaid?: () => Promise<void> | void;
    onClose?: () => void;
}

const TabAddPayment = ({
    objectId,
    generationId,
    activeTab,
    setActiveTab,
    recommendAmount,
    isUsd,
    fetchPayments,
    onPaid,
    onClose,
}: Props) => {
    const { t } = useTranslation(['Objects', 'common']);
    const { showToast } = useToast();
    const params = useSettingsParams();
    const { fetchObject } = useObjectCard();
    const refInputPaymentAmount = useRef<HTMLInputElement>(null);
    const [pendingCloudFiles, setPendingCloudFiles] = useState<File[]>([]);
    const [cloudProgress, setCloudProgress] = useState<number | boolean>(false);

    const uploadCloudFiles = async (): Promise<LeadCloudFileType[]> => {
        if (!pendingCloudFiles.length) return [];

        const filesMeta = pendingCloudFiles.map((file) => ({
            fileName: file.name,
            contentType: file.type || 'application/octet-stream',
            fileSize: file.size,
        }));

        const uploadConfigs = await getCloudflareUploadUrls(filesMeta, {
            objectId: Number(objectId || 0),
        });
        if (!uploadConfigs || uploadConfigs.length !== pendingCloudFiles.length) {
            throw new Error('Upload URLs are not available');
        }

        const uploaded = await uploadFilesWithOverallProgress(pendingCloudFiles, uploadConfigs, setCloudProgress);
        setCloudProgress(100);
        const cloudFiles = uploaded.filter((item) => item.success).map((item) => item.config);
        if (cloudFiles.length) {
            await linkCloudflareFiles(cloudFiles, { objectId: Number(objectId || 0) });
        }
        return cloudFiles;
    };

    const handleSubmit = async (values: PaymentFormValues, { resetForm }: FormikHelpers<PaymentFormValues>) => {
        const amount = safeFloatParse(values.paymentAmount || 0);
        if (!amount) {
            showToast(t('documents.payments.amountRequired'), 'warning');
            return;
        }

        let attachUrls: string[] = [];
        if (pendingCloudFiles.length) {
            try {
                setCloudProgress(1);
                const cloudFiles = await uploadCloudFiles();
                attachUrls = cloudFiles.map((file) => String(file.publicUrl || '')).filter(Boolean);
                if (!attachUrls.length) {
                    showToast(t('common:messError'), 'warning');
                }
            } catch (e) {
                console.error(e);
                showToast(t('common:messError'), 'warning');
            } finally {
                setCloudProgress(false);
            }
        }

        const resPayment = await apiDocument.addObjectDocumentPayment(objectId, generationId, {
            paymentAmount: amount,
            description: values.description,
            attach: attachUrls,
        });
        if (!resPayment?.stat || !resPayment?.paymentId) {
            showToast(t('common:messError'), 'danger');
            return;
        }

        if (params.objectPaymentControl && params.autoSyncPayStatus) {
            await apiObject.syncPayStatusIfCovered(objectId);
        }
        if (fetchObject) await fetchObject();
        if (onPaid) await onPaid();
        showToast(t('documents.payments.messSuccess'), 'success');
        resetForm();
        setPendingCloudFiles([]);
        onClose?.();
    };

    useEffect(() => {
        if (activeTab === 'add-payment' && refInputPaymentAmount.current) {
            refInputPaymentAmount.current.focus();
            refInputPaymentAmount.current.select();
        }
    }, [activeTab]);

    return (
        <div className="py-3">
            <Formik
                initialValues={{
                    paymentAmount: recommendAmount > 0 ? recommendAmount : 0,
                    description: '',
                }}
                enableReinitialize={true}
                onSubmit={handleSubmit}
            >
                {(formik) => (
                    <form onSubmit={formik.handleSubmit}>
                        <Form.Group className="mb-3">
                            <label className="form-label text-muted">
                                {t('documents.payments.labelAmount')}
                            </label>
                            <InputPrice
                                ref={refInputPaymentAmount}
                                disabled={formik.isSubmitting || cloudProgress !== false}
                                allowNegative
                                prefix={isUsd ? getBaseCurrencyPrefix() : getLocalCurrencyPrefix()}
                                value={formik.values.paymentAmount}
                                onValueChange={(val) =>
                                    formik.setFieldValue('paymentAmount', safeFloatParse(val.value || 0))
                                }
                            /> 
                            <div className="small text-muted mt-1">
                                {t('documents.payments.recommendHint', {
                                    amount: formatPrice(recommendAmount > 0 ? recommendAmount : 0),
                                    currency: isUsd ? getBaseCurrencySymbol() : getCurrencySymbol(),
                                })}
                            </div>
                            <div className="small text-muted mt-1">
                                {t('documents.payments.negativeHint')}
                            </div>
                        </Form.Group>

                        <FilesCloudUpload
                            disabled={formik.isSubmitting}
                            progress={cloudProgress}
                            files={pendingCloudFiles}
                            setFiles={setPendingCloudFiles}
                            label={t('common:labelAttachments')}
                            buttonLabel={t('common:buttons.upload')}
                            uploadingLabel={t('common:buttons.uploading')}
                        />

                        <Form.Group className="mb-3">
                            <label className="form-label text-muted">{t('documents.payments.labelDescription')}</label>
                            <Field
                                disabled={formik.isSubmitting || cloudProgress !== false}
                                name="description"
                                as="textarea"
                                rows="4"
                                className="form-control"
                            />
                        </Form.Group>

                        <div className="text-end">
                            <Button
                                type="submit"
                                size="sm"
                                variant="primary"
                                disabled={formik.isSubmitting || cloudProgress !== false}
                            >
                                {spinnerTitle(
                                    <>
                                        <SolarIcon name="plus-lg" className="me-1 fs-14" />
                                        {t('documents.payments.btnAdd')}
                                    </>,
                                    formik.isSubmitting || cloudProgress !== false,
                                    t('common:buttons.saving'),
                                )}
                            </Button>
                        </div>
                    </form>
                )}
            </Formik>
        </div>
    );
};

export default TabAddPayment;
